import './bootstrap'
import '../css/app.css'
import 'intl-tel-input/build/css/intlTelInput.css'
import { createApp, h } from 'vue'
import VueKonva from 'vue-konva'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import mitt from 'mitt'
import { modal } from 'momentum-modal'
import { createPinia } from 'pinia'
import { createInertiaApp } from '@inertiajs/vue3'
import * as Sentry from '@sentry/vue'
import { draggable } from '@/Lib/directives/draggable'
import { tooltip } from '@/Lib/directives/tooltip'
import { ZiggyVue } from '../../vendor/tightenco/ziggy/dist/vue.m'
import { Icons } from './Lib/icons'

library.add(Icons)

const appName =
    window.document.getElementsByTagName('title')[0]?.innerText || 'Punchlist'

createInertiaApp({
    title: (title) => `${appName} | ${title}`,
    resolve: (name) =>
        resolvePageComponent(
            `./Pages/${name}.vue`,
            import.meta.glob('./Pages/**/*.vue')
        ),
    progress: {
        color: '#4338ca',
    },
    setup({ el, App, props, plugin }) {
        const pinia = createPinia()

        const app = createApp({ render: () => h(App, props) })

        // Initialize Sentry with Vue app if DSN is available
        const sentryDsn =
            import.meta.env.VITE_SENTRY_DSN ||
            import.meta.env.VITE_SENTRY_LARAVEL_DSN
        const enableSentryInLocal =
            import.meta.env.VITE_SENTRY_ENABLE_LOCAL === 'true'
        const currentEnv = import.meta.env.VITE_APP_ENV || import.meta.env.MODE
        const isProductionEnv = ['production', 'staging'].includes(currentEnv)

        if (sentryDsn && (isProductionEnv || enableSentryInLocal)) {
            Sentry.init({
                app,
                dsn: sentryDsn,
                environment: currentEnv,
                release: __APP_VERSION__,
                integrations: [
                    Sentry.vueIntegration({
                        tracingOptions: {
                            trackComponents: true,
                            hooks: ['mount', 'update', 'unmount'],
                        },
                    }),
                    Sentry.browserTracingIntegration(),
                ],
                // Performance Monitoring
                tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE
                    ? parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE)
                    : enableSentryInLocal
                      ? 1.0 // 100% in local for testing
                      : 0.1,
                // Set sample rate for profiling
                profilesSampleRate: import.meta.env
                    .VITE_SENTRY_PROFILES_SAMPLE_RATE
                    ? parseFloat(
                          import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE
                      )
                    : undefined,
                // Enable debug mode for local testing (set to false to reduce console noise)
                // When true, logs all Sentry events including spans - can be very verbose
                debug: false,
                // Optional: Log errors to console when they're sent to Sentry
                beforeSend(event, hint) {
                    if (enableSentryInLocal) {
                        console.log(
                            'Sentry Error:',
                            event.error?.message || event.message,
                            event
                        )
                    }
                    return event
                },
            })
        }

        return app
            .component('font-awesome-icon', FontAwesomeIcon)
            .mixin({
                methods: {
                    asset: window.Vapor.asset,
                },
            })
            .directive('draggable', {
                beforeMount(el, binding) {
                    draggable(el, binding)
                },
            })
            .directive('tooltip', {
                beforeMount(el, binding) {
                    tooltip(el, binding)
                },
            })
            .provide('bus', mitt())
            .use(pinia)
            .use(ZiggyVue, Ziggy)
            .use(modal, {
                resolve: (name) =>
                    resolvePageComponent(
                        `./Pages/${name}.vue`,
                        import.meta.glob('./Pages/**/*.vue')
                    ),
            })
            .use(plugin)
            .use(
                ['production'].includes(import.meta.env.VITE_APP_ENV)
                    ? heap
                    : () => {}
            )
            .use(
                ['production', 'staging'].includes(import.meta.env.VITE_APP_ENV)
                    ? bugsnagVue
                    : () => {}
            )
            .use(VueKonva)
            .mount(el)
    },
})
